// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useState } from 'react';

import {
  type MessageDetails,
  type MessageId,
  type PriorityId,
  type MessageStatus,
  type EvolveApiParams,
  formatDate,
  useEvolvePaginatedGet,
} from '@evolvemep/api-ts';
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Flex,
  getThemeColor,
  Indicator,
  Loader,
  Menu,
  Stack,
  Tabs,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
// oxlint-disable-next-line no-restricted-imports
import { useNavigate, useParams } from 'react-router';

import { DesktopLayoutCard } from 'app/Layout/DesktopLayoutCard';
import { MobileUnsupportedGate } from 'app/Layout/MobileUnsupportedGate';
import { type HTMLRendererProps, HTMLRenderer } from 'app/MessageCenter/HTMLRenderer';
import type { TabOption } from 'app/MessageCenter/types';
import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { MESSAGE_PRIORITIES } from 'constants/globalConstants';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';

import { CreateMessageModal } from './CreateMessageModal';
import { MessagePreview } from './MessagePreview';
import { useMessages } from './MessagesContext';

const MessageContents = ({
  messageId,
  allowScripts,
}: { messageId: MessageId | null } & Pick<HTMLRendererProps, 'allowScripts'>) => {
  const theme = useMantineTheme();
  const [message, setMessage] = useState<MessageDetails>();
  const { getMessageDetails, gettingMessageDetails } = useMessages();
  useEffect(() => {
    if (messageId) {
      void getMessageDetails(messageId).then(setMessage);
    }
  }, [getMessageDetails, messageId]);

  return (
    <Flex align="center" justify="center" style={{ flex: 1 }} h="100%">
      {gettingMessageDetails || isNil(message) ? (
        <Loader size="md" />
      ) : (
        <Stack h="100%" w="100%" p="lg">
          <Text fz={25} fw={600}>
            {message.subject}
          </Text>
          <Text fw={600} c="gray">
            {formatDate(message.createdOn, 'MMMM dd, yyyy')}
          </Text>
          {message.messagePriorityCode === 'HIGH' && (
            <Card
              w="100%"
              p={6}
              withBorder
              bg={`${theme.primaryColor}.0`}
              style={{ borderColor: getThemeColor(theme.primaryColor, theme) }}
            >
              <Text fz="sm">
                <EvolveIcon spanProps={{ style: { marginTop: 2 } }} size="sm" icon="Alert" inline color="primary" />{' '}
                This message was sent with high priority.
              </Text>
            </Card>
          )}
          <HTMLRenderer html={message.body} allowScripts={allowScripts} />
        </Stack>
      )}
    </Flex>
  );
};

export const MESSAGE_CENTER_TAB_FILTER_MAP: Record<TabOption, EvolveApiParams<'GET_PAGINATED', 'admin/message'>> = {
  Messages: { hasMessageType: true, status: 'Active Dismissed' },
  Activity: { hasMessageType: false, status: 'Active Dismissed' },
  Archived: { status: 'Archived' },
};

export const MessageCenterPage = () => {
  const [filterMenuOpened, setFilterMenuOpened] = useState(false);
  const [filter, setFilter] = useState<{
    filterName: string;
    messagePriorityId?: PriorityId;
    status?: MessageStatus;
    read?: 'all' | 'unread';
  }>();
  const [currentTab, setCurrentTab] = useState<TabOption>('Messages');
  const [currentlyViewing, setCurrentlyViewing] = useState<MessageId | null>(null);

  const theme = useMantineTheme();
  // useEvolveNavigate only works inside Shop routes
  const navigate = useNavigate();
  const activeTabColor = getThemeColor(theme.primaryColor, theme);

  const { messageId } = useParams();
  useEffect(() => {
    if (isNotNil(messageId)) {
      setCurrentlyViewing(messageId as MessageId);
      void navigate('/message', { replace: true });
    }
  }, [messageId, navigate]);

  const {
    data: messages,
    entireCount,
    searchHandler,
    reset,
    fetchNextPage,
    loading,
    setDefaultOpts,
  } = useEvolvePaginatedGet('admin/message');

  useEffect(() => {
    const { filterName: _, ...filterParams } = filter ?? {};
    setDefaultOpts({
      perPage: 50,
      defaultConfig: {
        params: {
          read: 'all',
          ...MESSAGE_CENTER_TAB_FILTER_MAP[currentTab],
          ...filterParams,
        },
      },
    });
  }, [currentTab, filter, setDefaultOpts]);

  const { markAllRead, markingRead } = useMessages();

  const doMarkAllRead = useCallback(() => {
    void markAllRead(currentTab === 'Messages').then(() => {
      notifications.show({
        title: 'Success',
        message: `All ${currentTab.toLowerCase()} marked as read`,
        color: 'green',
      });
      reset();
    });
  }, [currentTab, reset, markAllRead]);

  return (
    <MobileUnsupportedGate>
      <DesktopLayoutCard>
        <Flex mb="sm" justify="space-between">
          <Button
            variant="light"
            size="sm"
            leftSection={<EvolveIcon icon="ArrowLeft" size="xs" />}
            onClick={() => navigate(-1)}
          >
            Go back
          </Button>
          <CreateMessageModal />
        </Flex>
        <Flex h="100%" style={{ overflowY: 'hidden' }}>
          <Card
            withBorder
            display="flex"
            p={0}
            h="100%"
            flex={1}
            miw={350}
            style={{ flexDirection: 'column', overflowY: 'hidden' }}
          >
            <Tabs
              value={currentTab}
              onChange={(e) => {
                setCurrentTab((t) => (e ?? t) as typeof currentTab);
                setCurrentlyViewing(null);
                setFilter(undefined);
              }}
              flex="0 0 auto"
              styles={{
                list: { flexWrap: 'nowrap' },
                tab: {
                  width: 110,
                  height: 50,
                  fontWeight: 600,
                },
                tabLabel: { fontWeight: 600 },
              }}
            >
              <Tabs.List>
                <Tabs.Tab
                  value="Messages"
                  flex={1}
                  leftSection={<EvolveIcon icon="Message" />}
                  style={{ color: currentTab === 'Messages' ? activeTabColor : theme.colors.gray[6] }}
                >
                  Messages
                </Tabs.Tab>
                <Tabs.Tab
                  value="Activity"
                  flex={1}
                  leftSection={<EvolveIcon icon="Notification" />}
                  style={{ flex: 1, color: currentTab === 'Activity' ? activeTabColor : theme.colors.gray[6] }}
                >
                  Activity
                </Tabs.Tab>
                <Tabs.Tab
                  value="Archived"
                  flex={1}
                  leftSection={<EvolveIcon icon="Archive" />}
                  style={{ color: currentTab === 'Archived' ? activeTabColor : theme.colors.gray[6] }}
                >
                  Archived
                </Tabs.Tab>
              </Tabs.List>
            </Tabs>
            <Stack p="xs" style={{ flex: '0 0 auto' }}>
              <Flex justify="flex-end" gap="xs" wrap="wrap">
                {currentTab !== 'Archived' && (
                  <Button variant="light" onClick={doMarkAllRead} disabled={loading} loading={markingRead}>
                    Mark all as read
                  </Button>
                )}
              </Flex>
              <Flex gap="xs" justify="space-between">
                <TextInputDebounced w="100%" placeholder="Search..." onChange={searchHandler} />
                <Menu
                  position="bottom-end"
                  shadow="sm"
                  opened={filterMenuOpened}
                  onClose={() => setFilterMenuOpened(false)}
                >
                  <AgGridStyleTooltip
                    label={isNotNil(filter) ? `Clear '${filter.filterName}' filter` : 'Filter'}
                    openDelay={100}
                    disabled={filterMenuOpened}
                  >
                    <Menu.Target>
                      <ActionIcon
                        onClick={() => {
                          if (isNotNil(filter)) {
                            setFilter(undefined);
                          } else {
                            setFilterMenuOpened(true);
                          }
                        }}
                        color={isNotNil(filter) ? theme.primaryColor : 'gray'}
                        size="lg"
                        variant="light"
                      >
                        <EvolveIcon icon={isNotNil(filter) ? 'ClearFilters' : 'Filter'} />
                      </ActionIcon>
                    </Menu.Target>
                  </AgGridStyleTooltip>
                  <Menu.Dropdown>
                    <Menu.Item
                      onClick={() =>
                        setFilter({
                          filterName: 'High priority',
                          messagePriorityId: MESSAGE_PRIORITIES.HIGH,
                        })
                      }
                    >
                      <Flex align="center" gap={4}>
                        <EvolveIcon icon="Alert" size="sm" color={theme.primaryColor} />
                        <Text>High priority</Text>
                      </Flex>
                    </Menu.Item>
                    {currentTab !== 'Archived' && (
                      <Menu.Item
                        onClick={() =>
                          setFilter({
                            filterName: 'Unread',
                            read: 'unread',
                          })
                        }
                      >
                        <Flex align="center" gap={4}>
                          <Indicator position="top-center">
                            <Box w={18} />
                          </Indicator>
                          <Text>Unread</Text>
                        </Flex>
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Flex>
            </Stack>
            <Flex direction="column" py="xs" gap="sm" px="md" style={{ overflowY: 'scroll' }} h="100%">
              {messages.map((m) => (
                <MessagePreview
                  key={m.messageId}
                  message={m}
                  canArchive={currentTab !== 'Archived'}
                  currentlyViewing={currentlyViewing}
                  setCurrentlyViewing={setCurrentlyViewing}
                  refreshAllMessages={reset}
                />
              ))}
              {entireCount === 0 && (
                <Text c="dimmed" fz="sm">
                  No messages matching current filter.
                </Text>
              )}
              <TriggerOnVisible loading={loading} onVisible={fetchNextPage} />
            </Flex>
          </Card>
          <Card withBorder flex={3} h="100%" ml={5}>
            {currentlyViewing ? (
              <MessageContents
                messageId={currentlyViewing}
                // All messages in "Messages" are fully generated by EVOLVE
                // Messages in "Activity" contain user input and cannot be trusted
                allowScripts={currentTab === 'Messages'}
              />
            ) : (
              <Flex justify="center" align="center" h="100%">
                {loading ? (
                  <Loader />
                ) : (
                  <Text fs="italic" c="gray">
                    {messages.length > 0 ? 'Select a message to view' : 'No messages to view'}
                  </Text>
                )}
              </Flex>
            )}
          </Card>
        </Flex>
      </DesktopLayoutCard>
    </MobileUnsupportedGate>
  );
};
